import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { Link } from 'react-router-dom';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

export default function PayrollsPage() {
  const navigate = useNavigate();
  const [payrolls, setPayrolls] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPayroll, setEditingPayroll] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPayment, setFilterPayment] = useState('all');
  const [deleteConfirmModal, setDeleteConfirmModal] = useState<any>(null);
  const [formData, setFormData] = useState({
    employee_id: '',
    employee_name: '',
    base_salary: 0,
    bonuses: 0,
    deductions: 0,
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'Transferencia',
    notes: ''
  });

  useEffect(() => {
    checkUser();
  }, []);

  const checkUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate('/admin/login');
    } else {
      loadData();
    }
    setLoading(false);
  };

  const loadData = async () => {
    const [payrollsRes, employeesRes] = await Promise.all([
      supabase.from('payrolls').select('*').order('payment_date', { ascending: false }),
      supabase.from('employees').select('*').eq('status', 'active')
    ]);
    setPayrolls(payrollsRes.data || []);
    setEmployees(employeesRes.data || []);
  };

  const handleEmployeeChange = (employeeId: string) => {
    const employee = employees.find(e => e.id === employeeId);
    if (employee) {
      setFormData({
        ...formData,
        employee_id: employeeId,
        employee_name: employee.name,
        base_salary: parseFloat(employee.salary)
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const net_salary = formData.base_salary + formData.bonuses - formData.deductions;
    
    if (editingPayroll) {
      const { error } = await supabase
        .from('payrolls')
        .update({
          employee_id: formData.employee_id || null,
          employee_name: formData.employee_name,
          base_salary: formData.base_salary,
          bonuses: formData.bonuses,
          deductions: formData.deductions,
          net_salary,
          payment_date: formData.payment_date,
          payment_method: formData.payment_method,
          notes: formData.notes
        })
        .eq('id', editingPayroll.id);

      if (!error) {
        handleCloseModal();
        loadData();
      }
    } else {
      const { error } = await supabase.from('payrolls').insert([{
        employee_id: formData.employee_id || null,
        employee_name: formData.employee_name,
        base_salary: formData.base_salary,
        bonuses: formData.bonuses,
        deductions: formData.deductions,
        net_salary,
        payment_date: formData.payment_date,
        payment_method: formData.payment_method,
        status: 'paid',
        notes: formData.notes
      }]);

      if (!error) {
        handleCloseModal();
        loadData();
      }
    }
  };

  const handleEdit = (payroll: any) => {
    setEditingPayroll(payroll);
    setFormData({
      employee_id: payroll.employee_id || '',
      employee_name: payroll.employee_name,
      base_salary: parseFloat(payroll.base_salary),
      bonuses: parseFloat(payroll.bonuses),
      deductions: parseFloat(payroll.deductions),
      payment_date: payroll.payment_date,
      payment_method: payroll.payment_method,
      notes: payroll.notes || ''
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingPayroll(null);
    setFormData({
      employee_id: '',
      employee_name: '',
      base_salary: 0,
      bonuses: 0,
      deductions: 0,
      payment_date: new Date().toISOString().split('T')[0],
      payment_method: 'Transferencia',
      notes: ''
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const handleDelete = async () => {
    if (!deleteConfirmModal) return;
    
    const { error } = await supabase
      .from('payrolls')
      .delete()
      .eq('id', deleteConfirmModal.id);

    if (!error) {
      setDeleteConfirmModal(null);
      loadData();
    }
  };

  // Calcular estadísticas
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();

  const monthPayroll = payrolls.filter(payroll => {
    const payrollDate = new Date(payroll.payment_date);
    return payrollDate.getMonth() === currentMonth && payrollDate.getFullYear() === currentYear;
  }).reduce((sum, payroll) => sum + parseFloat(payroll.net_salary), 0);

  const averageSalary = payrolls.length > 0 
    ? payrolls.reduce((sum, payroll) => sum + parseFloat(payroll.net_salary), 0) / payrolls.length 
    : 0;

  const totalBonuses = payrolls.reduce((sum, payroll) => sum + parseFloat(payroll.bonuses), 0);

  // Filtrar nóminas
  const filteredPayrolls = payrolls.filter(payroll => {
    const employee = employees.find(e => e.id === payroll.employee_id);
    const matchesSearch = searchQuery === '' || 
      (employee?.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      payroll.employee_name.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesPayment = filterPayment === 'all' || 
      (filterPayment === 'cash' && payroll.payment_method === 'Efectivo') ||
      (filterPayment === 'transfer' && payroll.payment_method === 'Transferencia') ||
      (filterPayment === 'check' && payroll.payment_method === 'Cheque');
    
    return matchesSearch && matchesPayment;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-16 h-16 border-4 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <AdminLayout currentPage="payrolls">
      <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Gestión de Nóminas</h2>
          <p className="text-xs sm:text-sm text-gray-600 mt-1">Administra los pagos de empleados</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="w-full sm:w-auto px-4 sm:px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap flex items-center justify-center gap-2 text-sm sm:text-base"
        >
          <i className="ri-add-line text-xl"></i>
          Nueva Nómina
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-4 sm:mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-money-dollar-circle-line text-xl sm:text-2xl text-green-600"></i>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs sm:text-sm text-gray-600">Nómina del Mes</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900 truncate">{formatCurrency(monthPayroll)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-file-list-line text-xl sm:text-2xl text-blue-600"></i>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs sm:text-sm text-gray-600">Total Nóminas</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{payrolls.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-bar-chart-line text-xl sm:text-2xl text-purple-600"></i>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs sm:text-sm text-gray-600">Promedio Salario</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900 truncate">{formatCurrency(averageSalary)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-yellow-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-gift-line text-xl sm:text-2xl text-yellow-600"></i>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs sm:text-sm text-gray-600">Total Bonificaciones</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900 truncate">{formatCurrency(totalBonuses)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row gap-3 sm:gap-4">
        <div className="flex-1">
          <div className="relative">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg"></i>
            <input
              type="text"
              placeholder="Buscar por empleado..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm"
            />
          </div>
        </div>
        <select
          value={filterPayment}
          onChange={(e) => setFilterPayment(e.target.value)}
          className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm font-medium whitespace-nowrap cursor-pointer"
        >
          <option value="all">Todos los métodos</option>
          <option value="cash">Efectivo</option>
          <option value="transfer">Transferencia</option>
          <option value="check">Cheque</option>
        </select>
      </div>

      {/* Payrolls Table - Desktop */}
      <div className="hidden lg:block bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Empleado</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Salario Base</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bonificaciones</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Deducciones</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Salario Neto</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Método Pago</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha Pago</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredPayrolls.map((payroll) => {
                const employee = employees.find(e => e.id === payroll.employee_id);
                return (
                  <tr key={payroll.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{employee?.name || 'N/A'}</div>
                      <div className="text-sm text-gray-500">{employee?.position || ''}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-900">{formatCurrency(payroll.base_salary)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-green-600">{formatCurrency(payroll.bonuses)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-red-600">{formatCurrency(payroll.deductions)}</td>
                    <td className="px-6 py-4 whitespace-nowrap font-semibold text-gray-900">{formatCurrency(payroll.net_salary)}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        payroll.payment_method === 'cash' ? 'bg-green-100 text-green-800' :
                        payroll.payment_method === 'transfer' ? 'bg-blue-100 text-blue-800' :
                        'bg-purple-100 text-purple-800'
                      }`}>
                        {payroll.payment_method === 'cash' ? 'Efectivo' : payroll.payment_method === 'transfer' ? 'Transferencia' : 'Cheque'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {new Date(payroll.payment_date).toLocaleDateString('es-CO')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => handleEdit(payroll)}
                          className="text-teal-600 hover:text-teal-700 font-medium cursor-pointer"
                        >
                          <i className="ri-edit-line text-lg"></i>
                        </button>
                        <button
                          onClick={() => setDeleteConfirmModal(payroll)}
                          className="text-red-600 hover:text-red-700 font-medium cursor-pointer"
                        >
                          <i className="ri-delete-bin-line text-lg"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Payrolls Cards - Mobile/Tablet */}
      <div className="lg:hidden space-y-4">
        {filteredPayrolls.map((payroll) => {
          const employee = employees.find(e => e.id === payroll.employee_id);
          return (
            <div key={payroll.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 truncate">{employee?.name || 'N/A'}</h3>
                  <p className="text-sm text-gray-600">{employee?.position || ''}</p>
                </div>
                <span className={`px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap ml-2 ${
                  payroll.payment_method === 'cash' ? 'bg-green-100 text-green-800' :
                  payroll.payment_method === 'transfer' ? 'bg-blue-100 text-blue-800' :
                  'bg-purple-100 text-purple-800'
                }`}>
                  {payroll.payment_method === 'cash' ? 'Efectivo' : payroll.payment_method === 'transfer' ? 'Transferencia' : 'Cheque'}
                </span>
              </div>
              
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <p className="text-xs text-gray-600">Salario Base</p>
                  <p className="font-semibold text-gray-900">{formatCurrency(payroll.base_salary)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600">Bonificaciones</p>
                  <p className="font-semibold text-green-600">{formatCurrency(payroll.bonuses)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600">Deducciones</p>
                  <p className="font-semibold text-red-600">{formatCurrency(payroll.deductions)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600">Salario Neto</p>
                  <p className="text-lg font-bold text-teal-600">{formatCurrency(payroll.net_salary)}</p>
                </div>
              </div>

              <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                <span className="text-xs text-gray-500">
                  {new Date(payroll.payment_date).toLocaleDateString('es-CO')}
                </span>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => handleEdit(payroll)}
                    className="text-teal-600 hover:text-teal-700 cursor-pointer"
                  >
                    <i className="ri-edit-line text-lg"></i>
                  </button>
                  <button
                    onClick={() => setDeleteConfirmModal(payroll)}
                    className="text-red-600 hover:text-red-700 cursor-pointer"
                  >
                    <i className="ri-delete-bin-line text-lg"></i>
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-900">
                {editingPayroll ? 'Editar Nómina' : 'Nueva Nómina'}
              </h3>
              <button onClick={handleCloseModal} className="text-gray-400 hover:text-gray-600">
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Empleado *</label>
                <select
                  required
                  value={formData.employee_id}
                  onChange={(e) => handleEmployeeChange(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent"
                  disabled={editingPayroll !== null}
                >
                  <option value="">Seleccionar empleado</option>
                  {employees.map(employee => (
                    <option key={employee.id} value={employee.id}>
                      {employee.name} - {employee.position}
                    </option>
                  ))}
                </select>
                {editingPayroll && (
                  <p className="text-xs text-gray-500 mt-1">No se puede cambiar el empleado al editar</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Salario Base *</label>
                <input
                  type="number"
                  required
                  min="0"
                  value={formData.base_salary}
                  onChange={(e) => setFormData({...formData, base_salary: parseFloat(e.target.value)})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Bonificaciones</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.bonuses}
                    onChange={(e) => setFormData({...formData, bonuses: parseFloat(e.target.value)})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Deducciones</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.deductions}
                    onChange={(e) => setFormData({...formData, deductions: parseFloat(e.target.value)})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Salario Neto:</span>
                  <span className="text-2xl font-bold text-teal-600">
                    {formatCurrency(formData.base_salary + formData.bonuses - formData.deductions)}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Fecha de Pago *</label>
                  <input
                    type="date"
                    required
                    value={formData.payment_date}
                    onChange={(e) => setFormData({...formData, payment_date: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Método de Pago *</label>
                  <select
                    value={formData.payment_method}
                    onChange={(e) => setFormData({...formData, payment_method: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent"
                  >
                    <option>Transferencia</option>
                    <option>Efectivo</option>
                    <option>Cheque</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Notas</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent"
                  placeholder="Información adicional sobre el pago..."
                ></textarea>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap"
                >
                  {editingPayroll ? 'Actualizar Nómina' : 'Registrar Nómina'}
                </button>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors whitespace-nowrap"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-error-warning-line text-2xl text-red-600"></i>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Eliminar Nómina</h3>
                <p className="text-sm text-gray-600">Esta acción no se puede deshacer</p>
              </div>
            </div>
            <p className="text-sm text-gray-700 mb-6">
              ¿Estás seguro de que deseas eliminar la nómina de <strong>{deleteConfirmModal.employee_name}</strong>?
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleDelete}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap"
              >
                Eliminar
              </button>
              <button
                onClick={() => setDeleteConfirmModal(null)}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors whitespace-nowrap"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
